import { forwardRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sidenav, Nav } from "rsuite";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { sidebarActions } from "../store/sidebar-slice";
import DashboardIcon from "@rsuite/icons/legacy/Dashboard";
import TableIcon from "@rsuite/icons/legacy/Table";
import DeviceIcon from "@rsuite/icons/Device";
import MobileIcon from "@rsuite/icons/Mobile";
import StorageIcon from "@rsuite/icons/Storage";
import AdminIcon from "@rsuite/icons/Admin";
import PeoplesIcon from "@rsuite/icons/Peoples";
import SiteIcon from "@rsuite/icons/Site";
import NoticeIcon from "@rsuite/icons/Notice";
import BellO from "@rsuite/icons/legacy/BellO";
import HandPointerIcon from "@rsuite/icons/legacy/HandPointerO";
import CalendarIcon from "@rsuite/icons/legacy/Calendar";
import { Icon } from "@rsuite/icons";
import { RiWindowsLine } from "react-icons/ri";
import { FaLinux } from "react-icons/fa";
import { FaApple } from "react-icons/fa";
import { SlLockOpen } from "react-icons/sl";
import { HiRocketLaunch, HiRectangleGroup } from "react-icons/hi2";
import { AiOutlineUserAdd } from "react-icons/ai";
import { BsDatabase } from "react-icons/bs";
import { BsDatabaseCheck } from "react-icons/bs";
import { BsWindow } from "react-icons/bs";
import { BsShieldLock, BsClipboardCheck, BsPersonBadge } from "react-icons/bs";
import { MdOutlineDevices } from "react-icons/md";
import { useAuth } from "../context/AuthContext";
import { can, canAny, CAPABILITIES } from "../util/Permissions";
import "./MainSidebar.css";

const Sidebar = () => {
  const [activeKey, setActiveKey] = useState();
  const [t] = useTranslation();
  const location = useLocation();
  const expanded = useSelector((state) => state.sidebar.isExpanded);
  const { user } = useAuth() ?? {};
  const dispatch = useDispatch();
  const customLink = forwardRef(({ href, children, ...rest }, ref) => (
    <Link ref={ref} to={href} {...rest}>
      {children}
    </Link>
  ));

  useEffect(() => {
    let pathname = location.pathname;

    switch (pathname) {
      case "/dashboard/mobile":
        setActiveKey("1-5");
        break;
      case "/dashboard/apple":
        setActiveKey("1-6");
        break;
      case "/dashboard/linux":
        setActiveKey("1-3");
        break;
      case "/dashboard/windows":
        setActiveKey("1-2");
        break;
      case "/dashboard/mission-control":
        setActiveKey("1-7");
        break;
      case "/dashboard/mission-control-2":
        setActiveKey("1-8");
        break;
      case "/community":
        setActiveKey("7-1");
        break;
      default:
        break;
    }
  }, [location]);

  const menuHandler = () => {
    dispatch(sidebarActions.toggle());
  };

  return (
    <Sidenav
      expanded={expanded}
      appearance="subtle"
      className="sidenav-container"
    >
      <Sidenav.Body>
        <Nav activeKey={activeKey} onSelect={setActiveKey}>
          <Nav.Menu
            eventKey="1"
            title={t("main-sidebar.dashboard")}
            icon={<DashboardIcon />}
            as="li"
            onClick={!expanded ? menuHandler : null}
          >
            <Nav.Item
              eventKey="1-1"
              icon={<DeviceIcon />}
              as={customLink}
              href="/dashboard"
            >
              {t("main-sidebar.all")}
            </Nav.Item>
            <Nav.Item
              eventKey="1-2"
              icon={<Icon as={RiWindowsLine} />}
              as={customLink}
              href="/dashboard/windows"
            >
              Windows
            </Nav.Item>
            <Nav.Item
              eventKey="1-3"
              icon={<Icon as={FaLinux} />}
              as={customLink}
              href="/dashboard/linux"
            >
              Linux
            </Nav.Item>
            <Nav.Item
              eventKey="1-4"
              icon={<StorageIcon />}
              as={customLink}
              href="/dashboard/servers"
            >
              {t("main-sidebar.servers")}
            </Nav.Item>
            <Nav.Item
              eventKey="1-5"
              icon={<MobileIcon />}
              as={customLink}
              href="/dashboard/mobile"
            >
              {"Android"}
            </Nav.Item>
            <Nav.Item
              eventKey="1-6"
              icon={<Icon as={FaApple} />}
              as={customLink}
              href="/dashboard/apple"
            >
              Apple
            </Nav.Item>
            <Nav.Item
              eventKey="1-7"
              icon={<Icon as={HiRocketLaunch} />}
              as={customLink}
              href="/dashboard/mission-control"
            >
              {t("main-sidebar.mission-control")}
            </Nav.Item>
            <Nav.Item
              eventKey="1-8"
              icon={<Icon as={HiRectangleGroup} />}
              as={customLink}
              href="/dashboard/mission-control-2"
            >
              {t("main-sidebar.mission-control-2")}
            </Nav.Item>
          </Nav.Menu>
          <Nav.Menu
            eventKey="2"
            title={t("main-sidebar.reports")}
            icon={<TableIcon />}
            as="li"
            onClick={!expanded ? menuHandler : null}
          >
            <Nav.Menu
              eventKey="2-1"
              title={t("main-sidebar.devices")}
              icon={<DeviceIcon />}
            >
              <Nav.Item
                eventKey="2-1-1"
                icon={<DeviceIcon />}
                as={customLink}
                href="/reports/devices/all"
              >
                {t("main-sidebar.all")}
              </Nav.Item>
            </Nav.Menu>
            <Nav.Menu
              eventKey="2-2"
              title={t("main-sidebar.users")}
              icon={<PeoplesIcon />}
            >
              <Nav.Item
                eventKey="2-2-1"
                icon={<Icon as={SlLockOpen} />}
                href="/reports/users/access"
              >
                {t("main-sidebar.access")}
              </Nav.Item>
            </Nav.Menu>
            <Nav.Item
              eventKey="2-3"
              icon={<Icon as={SlLockOpen} />}
              as={customLink}
              href="/reports/mobile-applications"
            >
              {t("main-sidebar.mobile-applications")}
            </Nav.Item>
          </Nav.Menu>
          <Nav.Menu
            eventKey="3"
            title={t("main-sidebar.actions")}
            icon={<HandPointerIcon />}
            as="li"
            onClick={!expanded ? menuHandler : null}
          >
            <Nav.Item
              eventKey="3-1"
              icon={<CalendarIcon />}
              as={customLink}
              href="/actions/calendar"
            >
              {t("main-sidebar.calendar")}
            </Nav.Item>
          </Nav.Menu>
          <Nav.Menu
            eventKey="4"
            title={t("main-sidebar.alarms")}
            icon={<NoticeIcon />}
            as="li"
            onClick={!expanded ? menuHandler : null}
          >
            <Nav.Item eventKey="4-1" icon={<NoticeIcon />}>
              {t("main-sidebar.global-alarms")}
            </Nav.Item>
            <Nav.Item
              eventKey="4-2"
              icon={<BellO />}
              as={customLink}
              href="/alarms/individual"
            >
              {t("main-sidebar.individual-alarms")}
            </Nav.Item>
          </Nav.Menu>
          <Nav.Menu
            eventKey="7"
            title={t("main-sidebar.community")}
            icon={<PeoplesIcon />}
            as="li"
            onClick={!expanded ? menuHandler : null}
          >
            <Nav.Item
              eventKey="7-1"
              icon={<SiteIcon />}
              as={customLink}
              href="/community"
            >
              {t("main-sidebar.virtual-house")}
            </Nav.Item>
          </Nav.Menu>
          {canAny(user, [
            CAPABILITIES.usersManage,
            CAPABILITIES.loginAttemptsView,
            CAPABILITIES.auditView,
            CAPABILITIES.sessionsManage,
            CAPABILITIES.permissionsManage,
            CAPABILITIES.servicesControl,
            CAPABILITIES.servicesLogs,
            CAPABILITIES.alarmsManage,
          ]) && (
            <Nav.Menu
              eventKey="5"
              title={t("main-sidebar.admin")}
              icon={<AdminIcon />}
              as="li"
              onClick={!expanded ? menuHandler : null}
            >
              {can(user, CAPABILITIES.usersManage) && (
                <Nav.Item
                  eventKey="5-1"
                  icon={<PeoplesIcon />}
                  as={customLink}
                  href="/admin/users"
                >
                  {t("main-sidebar.users")}
                </Nav.Item>
              )}
              {can(user, CAPABILITIES.usersManage) && (
                <Nav.Item
                  eventKey="5-2"
                  icon={<Icon as={AiOutlineUserAdd} />}
                  as={customLink}
                  href="/admin/new-user"
                >
                  {t("main-sidebar.new-user")}
                </Nav.Item>
              )}
              {can(user, CAPABILITIES.loginAttemptsView) && (
                <Nav.Item
                  eventKey="5-6"
                  icon={<Icon as={BsShieldLock} />}
                  as={customLink}
                  href="/admin/login-attempts"
                >
                  {t("main-sidebar.login-attempts")}
                </Nav.Item>
              )}
              {can(user, CAPABILITIES.auditView) && (
                <Nav.Item
                  eventKey="5-7"
                  icon={<Icon as={BsClipboardCheck} />}
                  as={customLink}
                  href="/admin/audit"
                >
                  {t("main-sidebar.audit-log")}
                </Nav.Item>
              )}
              {can(user, CAPABILITIES.sessionsManage) && (
                <Nav.Item
                  eventKey="5-8"
                  icon={<Icon as={MdOutlineDevices} />}
                  as={customLink}
                  href="/admin/sessions"
                >
                  {t("main-sidebar.active-sessions")}
                </Nav.Item>
              )}
              {can(user, CAPABILITIES.permissionsManage) && (
                <Nav.Item
                  eventKey="5-9"
                  icon={<Icon as={BsPersonBadge} />}
                  as={customLink}
                  href="/admin/permissions"
                >
                  {t("main-sidebar.permissions")}
                </Nav.Item>
              )}
              {can(user, CAPABILITIES.alarmsManage) && (
                <Nav.Item
                  eventKey="5-10"
                  icon={<BellO />}
                  as={customLink}
                  href="/admin/alarm-recipients"
                >
                  {t("main-sidebar.alarm-recipients")}
                </Nav.Item>
              )}
              {can(user, CAPABILITIES.servicesControl) && (
                <Nav.Item
                  eventKey="5-3"
                  icon={<Icon as={BsDatabase} />}
                  as={customLink}
                  href="/admin/services"
                >
                  {t("main-sidebar.service-control")}
                </Nav.Item>
              )}
              {can(user, CAPABILITIES.servicesLogs) && (
                <Nav.Item
                  eventKey="5-4"
                  icon={<Icon as={BsDatabaseCheck} />}
                  as={customLink}
                  href="/admin/core-logs"
                >
                  {t("main-sidebar.check-core-api-logs")}
                </Nav.Item>
              )}
              {can(user, CAPABILITIES.servicesLogs) && (
                <Nav.Item
                  eventKey="5-5"
                  icon={<Icon as={BsDatabaseCheck} />}
                  as={customLink}
                  href="/admin/cmd-logs"
                >
                  {t("main-sidebar.check-cmd-api-logs")}
                </Nav.Item>
              )}
            </Nav.Menu>
          )}
          <Nav.Menu
            eventKey="6"
            title="Modals"
            icon={<SiteIcon />}
            as="li"
            onClick={!expanded ? menuHandler : null}
          >
            <Nav.Item
              eventKey="6-1"
              icon={<Icon as={BsWindow} />}
              as={customLink}
              href="/modals"
            >
              Dialogs
            </Nav.Item>
          </Nav.Menu>
        </Nav>
      </Sidenav.Body>
    </Sidenav>
  );
};

export default Sidebar;
